import React, { useState, useEffect, useRef } from 'react';
import { Camera, Upload, Download, BarChart3, Map, Settings, Sun, Moon, ChevronRight, Plus, Trash2, Eye, CheckCircle, AlertCircle, TrendingUp, Target, MapPin } from 'lucide-react';
import './App.css';

const CampaignFlow = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('setup');
  const [leafletType, setLeafletType] = useState('');
  const [sentiments, setSentiments] = useState(['Positive', 'Negative', 'Neutral']);
  const [voterIntentions, setVoterIntentions] = useState(['Conservative', 'Labour', 'Liberal Democrat', 'Undecided']);
  const [exportFields, setExportFields] = useState(['Name', 'Address', 'Phone', 'Polling Number']);
  const [scannedLeaflets, setScannedLeaflets] = useState([]);
  const [newSentiment, setNewSentiment] = useState('');
  const [newVoterIntention, setNewVoterIntention] = useState('');
  const [newExportField, setNewExportField] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const leafletTypes = [
    { id: 'door-hanger', name: 'Door Hanger Survey', description: 'Standard door-to-door canvas form' },
    { id: 'event-survey', name: 'Event Survey', description: 'Town hall or event feedback form' },
    { id: 'phone-survey', name: 'Phone Survey', description: 'Telephone canvas response sheet' },
    { id: 'mail-survey', name: 'Mail Survey', description: 'Postal survey response form' }
  ];

  const generateFakeLeaflet = () => {
    const names = ['John Smith', 'Sarah Johnson', 'Mike Williams', 'Emma Davis', 'James Wilson', 'Lisa Brown'];
    const addresses = ['123 Oak St', '456 Maple Ave', '789 Pine Rd', '321 Elm Dr', '654 Cedar Ln', '987 Birch Way'];
    const sentimentOptions = ['Positive', 'Negative', 'Neutral', 'Mixed'];
    const intentionOptions = ['Conservative', 'Labour', 'Liberal Democrat', 'Undecided', 'Green', 'Independent'];
    const issues = ['Healthcare', 'Economy', 'Education', 'Environment', 'Immigration', 'Crime'];
    
    return {
      id: Date.now() + Math.random(),
      name: names[Math.floor(Math.random() * names.length)],
      address: addresses[Math.floor(Math.random() * addresses.length)],
      pollingNumber: Math.floor(Math.random() * 9999) + 1000,
      phone: `(555) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
      sentiment: sentimentOptions[Math.floor(Math.random() * sentimentOptions.length)],
      voterIntention: intentionOptions[Math.floor(Math.random() * intentionOptions.length)],
      keyIssues: issues.slice(0, Math.floor(Math.random() * 3) + 1),
      confidence: Math.floor(Math.random() * 30) + 70,
      timestamp: new Date().toISOString()
    };
  };

  const handleCameraCapture = () => {
    const fakeLeaflet = generateFakeLeaflet();
    setScannedLeaflets(prev => [...prev, fakeLeaflet]);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newLeaflets = files.map(() => generateFakeLeaflet());
    setScannedLeaflets(prev => [...prev, ...newLeaflets]);
  };

  const addSentiment = () => {
    if (newSentiment.trim()) {
      setSentiments(prev => [...prev, newSentiment.trim()]);
      setNewSentiment('');
    }
  };

  const addVoterIntention = () => {
    if (newVoterIntention.trim()) {
      setVoterIntentions(prev => [...prev, newVoterIntention.trim()]);
      setNewVoterIntention('');
    }
  };

  const addExportField = () => {
    if (newExportField.trim()) {
      setExportFields(prev => [...prev, newExportField.trim()]);
      setNewExportField('');
    }
  };

  const generateCSV = () => {
    const headers = exportFields.join(',');
    const rows = scannedLeaflets.map(leaflet => 
      exportFields.map(field => {
        const fieldMap = {
          'Name': leaflet.name,
          'Address': leaflet.address,
          'Phone': leaflet.phone,
          'Polling Number': leaflet.pollingNumber,
          'Sentiment': leaflet.sentiment,
          'Voter Intention': leaflet.voterIntention,
          'Key Issues': leaflet.keyIssues.join('; '),
          'Confidence': leaflet.confidence + '%'
        };
        return `"${fieldMap[field] || ''}"`;
      }).join(',')
    ).join('\n');
    
    const csvContent = headers + '\n' + rows;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign-data-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatsData = () => {
    const total = scannedLeaflets.length;
    const sentimentCounts = sentiments.reduce((acc, sentiment) => {
      acc[sentiment] = scannedLeaflets.filter(l => l.sentiment === sentiment).length;
      return acc;
    }, {});
    
    const intentionCounts = voterIntentions.reduce((acc, intention) => {
      acc[intention] = scannedLeaflets.filter(l => l.voterIntention === intention).length;
      return acc;
    }, {});

    return { total, sentimentCounts, intentionCounts };
  };

  const SetupScreen = () => (
    <div className="screen-content">
      <div className="setup-grid">
        <div className="setup-card">
          <h3>Select Leaflet Type</h3>
          <div className="leaflet-types">
            {leafletTypes.map(type => (
              <div 
                key={type.id} 
                className={`leaflet-type ${leafletType === type.id ? 'selected' : ''}`}
                onClick={() => setLeafletType(type.id)}
              >
                <h4>{type.name}</h4>
                <p>{type.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="setup-card">
          <h3>Configure Sentiments</h3>
          <div className="config-list">
            {sentiments.map((sentiment, index) => (
              <div key={index} className="config-item">
                <span>{sentiment}</span>
                <button onClick={() => setSentiments(prev => prev.filter((_, i) => i !== index))}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <div className="add-item">
              <input
                type="text"
                placeholder="Add sentiment..."
                value={newSentiment}
                onChange={(e) => setNewSentiment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addSentiment()}
              />
              <button onClick={addSentiment}><Plus size={16} /></button>
            </div>
          </div>
        </div>

        <div className="setup-card">
          <h3>Configure Voter Intentions</h3>
          <div className="config-list">
            {voterIntentions.map((intention, index) => (
              <div key={index} className="config-item">
                <span>{intention}</span>
                <button onClick={() => setVoterIntentions(prev => prev.filter((_, i) => i !== index))}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <div className="add-item">
              <input
                type="text"
                placeholder="Add voter intention..."
                value={newVoterIntention}
                onChange={(e) => setNewVoterIntention(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addVoterIntention()}
              />
              <button onClick={addVoterIntention}><Plus size={16} /></button>
            </div>
          </div>
        </div>

        <div className="setup-card">
          <h3>Export Fields</h3>
          <div className="config-list">
            {exportFields.map((field, index) => (
              <div key={index} className="config-item">
                <span>{field}</span>
                <button onClick={() => setExportFields(prev => prev.filter((_, i) => i !== index))}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <div className="add-item">
              <input
                type="text"
                placeholder="Add export field..."
                value={newExportField}
                onChange={(e) => setNewExportField(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addExportField()}
              />
              <button onClick={addExportField}><Plus size={16} /></button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="setup-actions">
        <button 
          className="primary-button"
          onClick={() => setCurrentScreen('scanning')}
          disabled={!leafletType}
        >
          Start Scanning <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );

  const ScanningScreen = () => (
    <div className="screen-content">
      <div className="scanning-header">
        <h2>Scan Leaflets</h2>
        <div className="scan-actions">
          <button className="scan-button camera" onClick={handleCameraCapture}>
            <Camera size={20} />
            Camera
          </button>
          <button className="scan-button upload" onClick={() => fileInputRef.current?.click()}>
            <Upload size={20} />
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      <div className="scanning-grid">
        {scannedLeaflets.map((leaflet, index) => (
          <div key={leaflet.id} className="leaflet-card">
            <div className="leaflet-preview">
              <div className="fake-leaflet">
                <div className="leaflet-header">Survey #{leaflet.pollingNumber}</div>
                <div className="leaflet-content">
                  <div className="leaflet-field">
                    <strong>Name:</strong> {leaflet.name}
                  </div>
                  <div className="leaflet-field">
                    <strong>Address:</strong> {leaflet.address}
                  </div>
                  <div className="leaflet-field">
                    <strong>Phone:</strong> {leaflet.phone}
                  </div>
                </div>
              </div>
            </div>
            <div className="ai-summary">
              <div className="summary-header">
                <CheckCircle className="success-icon" size={16} />
                AI Analysis ({leaflet.confidence}% confidence)
              </div>
              <div className="summary-content">
                <div className="summary-item">
                  <strong>Sentiment:</strong> 
                  <span className={`sentiment-badge ${leaflet.sentiment.toLowerCase()}`}>
                    {leaflet.sentiment}
                  </span>
                </div>
                <div className="summary-item">
                  <strong>Voter Intention:</strong> 
                  <span className="intention-badge">{leaflet.voterIntention}</span>
                </div>
                <div className="summary-item">
                  <strong>Key Issues:</strong> {leaflet.keyIssues.join(', ')}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="scanning-footer">
        <div className="scan-stats">
          <span>Scanned: {scannedLeaflets.length} leaflets</span>
        </div>
        <div className="scan-actions">
          <button className="secondary-button" onClick={generateCSV} disabled={scannedLeaflets.length === 0}>
            <Download size={20} />
            Export CSV
          </button>
          <button className="primary-button" onClick={() => setCurrentScreen('dashboard')}>
            View Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  const DashboardScreen = () => {
    const stats = getStatsData();
    
    return (
      <div className="screen-content">
        <div className="dashboard-header">
          <h2>Campaign Dashboard</h2>
          <div className="dashboard-stats">
            <div className="stat-card">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Responses</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{Object.values(stats.sentimentCounts).reduce((a, b) => a + b, 0)}</div>
              <div className="stat-label">Processed</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {stats.total > 0 ? Math.round((stats.sentimentCounts.Positive || 0) / stats.total * 100) : 0}%
              </div>
              <div className="stat-label">Positive</div>
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>Sentiment Analysis</h3>
            <div className="chart-container">
              <div className="bar-chart">
                {Object.entries(stats.sentimentCounts).map(([sentiment, count]) => (
                  <div key={sentiment} className="bar-item">
                    <div className="bar-label">{sentiment}</div>
                    <div className="bar-container">
                      <div 
                        className={`bar ${sentiment.toLowerCase()}`}
                        style={{ width: `${stats.total > 0 ? (count / stats.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <div className="bar-value">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Voter Intentions</h3>
            <div className="chart-container">
              <div className="bar-chart">
                {Object.entries(stats.intentionCounts).map(([intention, count]) => (
                  <div key={intention} className="bar-item">
                    <div className="bar-label">{intention}</div>
                    <div className="bar-container">
                      <div 
                        className="bar intention"
                        style={{ width: `${stats.total > 0 ? (count / stats.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <div className="bar-value">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="dashboard-card map-card">
            <h3>Area Heat Map</h3>
            <div className="map-container">
              <div className="fake-map">
                <div className="map-region high-positive">North District</div>
                <div className="map-region medium-positive">Central District</div>
                <div className="map-region low-positive">East District</div>
                <div className="map-region negative">South District</div>
                <div className="map-region neutral">West District</div>
              </div>
              <div className="map-legend">
                <div className="legend-item">
                  <div className="legend-color high-positive"></div>
                  <span>Highly Favorable</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color medium-positive"></div>
                  <span>Favorable</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color neutral"></div>
                  <span>Neutral</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color negative"></div>
                  <span>Needs Attention</span>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card recommendations-card">
            <h3>AI Recommendations</h3>
            <div className="recommendations">
              <div className="recommendation">
                <Target className="rec-icon" size={16} />
                <div className="rec-content">
                  <h4>Focus on South District</h4>
                  <p>Lower sentiment scores suggest need for increased canvassing efforts</p>
                </div>
              </div>
              <div className="recommendation">
                <TrendingUp className="rec-icon" size={16} />
                <div className="rec-content">
                  <h4>Healthcare Messaging</h4>
                  <p>Top concern across all districts - emphasize healthcare policy</p>
                </div>
              </div>
              <div className="recommendation">
                <MapPin className="rec-icon" size={16} />
                <div className="rec-content">
                  <h4>Expand in North District</h4>
                  <p>High positive sentiment - opportunity for volunteer recruitment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="app">
      <div className="background-pattern"></div>
      
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <h1>CampaignFlow</h1>
            <span className="beta">BETA</span>
          </div>
          
          <nav className="nav">
            <button 
              className={currentScreen === 'setup' ? 'active' : ''}
              onClick={() => setCurrentScreen('setup')}
            >
              <Settings size={20} />
              Setup
            </button>
            <button 
              className={currentScreen === 'scanning' ? 'active' : ''}
              onClick={() => setCurrentScreen('scanning')}
            >
              <Camera size={20} />
              Scanning
            </button>
            <button 
              className={currentScreen === 'dashboard' ? 'active' : ''}
              onClick={() => setCurrentScreen('dashboard')}
            >
              <BarChart3 size={20} />
              Dashboard
            </button>
          </nav>
          
          <button className="theme-toggle" onClick={() => setDarkMode(!darkMode)}>
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
      </header>

      <main className="main">
        {currentScreen === 'setup' && <SetupScreen />}
        {currentScreen === 'scanning' && <ScanningScreen />}
        {currentScreen === 'dashboard' && <DashboardScreen />}
      </main>
    </div>
  );
};

export default CampaignFlow;